"use client";

import Image from "next/image";
import { SUPPORT_EMAIL } from "src/lib/product-name";

interface BlueberryVisitErrorProps {
  /**
   * Short, user-facing context about what went wrong — appended below the
   * generic copy. Keep to a single sentence.
   */
  reason?: string;
  /**
   * Technical detail (error message, ids) — rendered in small muted text for
   * support and debugging. Safe to include raw error strings.
   */
  detail?: string;
}

/**
 * Full-screen Owlet OnCall error state for Blueberry visit flows (token failure,
 * intake timeout). Matches Figma error treatment: dark canvas, branded header,
 * unplugged illustration, and support copy — no primary action (user closes app).
 */
export default function BlueberryVisitError({
  reason,
  detail,
}: BlueberryVisitErrorProps = {}) {
  return (
    <div className="flex min-h-[calc(100vh-var(--felt-safe-area-top,0px)-var(--felt-safe-area-bottom,0px))] flex-col bg-[#060727]">
      <header className="flex flex-col items-center gap-2 px-4 pt-[calc(var(--felt-safe-area-top,0px)+16px)]">
        <Image
          src="/icons/owlet/logo.png"
          alt="Owlet OnCall"
          height={18}
          width={128}
          priority
        />
        <p className="text-center font-source-sans text-xs font-semibold leading-4 text-[#7d80e6]">
          Pediatricians 24/7 in under an hour
        </p>
      </header>

      <div className="flex flex-1 flex-col items-center justify-center px-4 pb-[var(--felt-safe-area-bottom,0px)]">
        <div className="flex w-[345px] max-w-full flex-col items-center gap-6">
          <Image
            src="/unplugged-icon.svg"
            alt=""
            width={129}
            height={86}
            priority
          />
          <h1 className="text-center font-source-serif text-xl font-semibold leading-6 text-[#d4d4f9]">
            Something went wrong
          </h1>
          <p className="text-center font-source-sans text-base font-normal leading-6 text-[#d4d4f9]">
            We couldn&apos;t load your information at the moment. Please close
            the app and try again. If the issue continues, contact support at{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className="underline">
              {SUPPORT_EMAIL}
            </a>
            .
          </p>
          {reason && (
            <p className="text-center font-source-sans text-sm font-normal leading-5 text-[#d4d4f9]/80">
              {reason}
            </p>
          )}
          {detail && (
            <p className="max-w-full text-center font-mono text-xs leading-4 text-[#d4d4f9]/50 break-words">
              {detail}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
